<script lang="ts">
  import { LoadingIndicatorSmall } from "./mod.ts";
  import { Center } from "../layouts/mod.ts";

  type Props =
    & {
      disabled?: boolean;
      type?:
        | "primary"
        | "secondary"
        | "ghost"
        | "white"
        | "warning"
        | "text-button";
      customClass?: string;
      outset?: boolean;
      full?: boolean;
      loading?: boolean;
      nominwidth?: boolean;
      text: string;
    }
    & (
      {
        onClick: () => void;
        link?: undefined;
      } | {
        onClick?: undefined;
        link: string;
      }
    );

  const {
    onClick,
    link,
    text,
    disabled = false,
    type = "primary",
    customClass = "",
    outset = true,
    full = false,
    loading = false,
    nominwidth = false,
  }: Props = $props();
</script>

{#snippet inner()}
  {#if loading}
    <Center>
      <span class="relative">
        <span class="absolute left-[calc(50%-12px)] top-[calc(50%-12px)]">
          <LoadingIndicatorSmall visible />
        </span>
      </span>
    </Center>
  {:else}
    {text}
  {/if}
{/snippet}

{#if onClick}
  <button
    class:outset
    class:loading
    class="button {type} {customClass}"
    class:w-full={full}
    onclick={onClick}
    {disabled}
    class:min-w-[150px]={!nominwidth}
  >
    {@render inner()}
  </button>
{:else}
  <a
    href={link}
    class:outset
    class:loading
    class="button {type} {customClass}"
    class:w-full={full}
    class:disabled
    class:min-w-[150px]={!nominwidth}
    onclick={onClick}
  >
    {@render inner()}
  </a>
{/if}

<style lang="postcss" scoped>
  .button {
    height: 40px;
    font-family: "Urbanist", sans-serif;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    font-size: 16px;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    padding-left: 25px;
    padding-right: 25px;
  }
  .loading {
    pointer-events: none;
  }
  /* Styling is found in app.css */

  .primary {
    @apply bg-green-45 text-grey-0;
  }
  .primary:hover {
    @apply bg-greenGrey-30 text-white;
  }
  .primary:active {
    @apply bg-green-50 text-red-50;
  }
  .primary:focus {
    @apply bg-greenGrey-30 text-white border-solid border-[1px] border-green-45;
  }

  .button:disabled, .button.disabled {
    @apply bg-grey-5 text-grey-20 border-grey-5 cursor-none pointer-events-none;
  }

  .secondary:disabled, .secondary.disabled {
    :global(.dark) & {
      @apply bg-[#383838] text-[#999999];
    }
  }
  /* Secondary */
  .secondary {
    @apply bg-green-72 text-white;

    :global(.dark) & {
      @apply bg-[#5b5b5b] text-[#dadada];
    }
  }
  .secondary:hover {
    @apply bg-green-55 text-white;

    :global(.dark) & {
      @apply bg-[#747474] text-[#dadada];
    }
  }
  .secondary:active {
    @apply bg-green-75 text-white;
  }
  .secondary:focus {
    @apply bg-green-55 text-white border-solid border-[1px] border-green-72;

    :global(.dark) & {
      @apply bg-[#747474] text-[#dadada] border-white;
    }
  }

  /* Ghost */
  .ghost {
    @apply bg-transparent text-greenGrey-60 border-solid border-[1px]
      border-greenGrey-10;
  }
  .ghost:hover {
    @apply bg-transparent text-greenGrey-60 border-solid border-[1px]
      border-greenGrey-40;
  }
  .ghost:active {
    @apply bg-transparent text-greenGrey-60 border-solid border-[1px]
      border-greenGrey-40;
  }
  .ghost:focus {
    @apply bg-transparent text-greenGrey-60 border-solid border-[1px]
      border-green-25;
  }

  /* White */
  .white {
    @apply bg-transparent text-white border-solid border-[1px] border-white;
  }
  .white:hover {
    @apply bg-transparent text-grey-20 border-solid border-[1px] border-white;
  }
  .white:active {
    @apply bg-transparent text-white border-solid border-[1px] border-white;
  }
  .white:focus {
    @apply bg-transparent text-white border-solid border-[1px] border-white;
  }

  /* Warning */
  .warning {
    @apply bg-[#ffffff] text-[#ff8980] border-solid border-[1px]
      border-[#dfedec];
  }
  .warning:hover {
    @apply bg-[#f9fcfd] text-[#ff8980] border-solid border-[1px]
      border-[#dfedec];
  }
  .warning:active {
    @apply bg-[#ffffff] text-[#ff8980] border-solid border-[1px]
      border-[#dfedec];
  }
  .warning:focus {
    @apply bg-[#ffffff] text-[#ff8980] border-solid border-[1px]
      border-[#dfedec];
  }
</style>
